import { useEffect, useState } from "react";

import { Flex } from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import { Input, FieldForm } from "components/RFQ/Input";
import { useField, useFormikContext } from "formik";
import { Button } from "components/Button";
import QuestionTypeTable from "components/Pages/QuestionType/QuestionTypeTable";
import { SmartContent } from "./_components/SmartContent";
import { QUESTION_TYPE_ID } from "newui/features/Rfx/Submission/constants";
import { QuestionTypeTableRowResponse } from "newui/features/Rfx/Creation/components/ReturnableSchedule/QuestionTypes/QuestionTypeTableRowResponse";
import { rfxSubmission } from "newui/features/Rfx/Submission";
import { TextLength } from "newui/features/Rfx/types/component-types";
import { getDefaultValueForQuestionType } from "newui/features/Rfx/Creation/data-processor";

const questionsOptions = [
	{
		value: QUESTION_TYPE_ID.SHORT_ANSWER,
		label: "Short Answer",
		icon: "short-answer",
	},
	{
		value: QUESTION_TYPE_ID.PARAGRAPH,
		label: "Paragraph",
		icon: "paragraph",
	},
	{
		value: QUESTION_TYPE_ID.SINGLE_OPTION,
		label: "Single Option",
		icon: "single-option",
	},
	{
		value: QUESTION_TYPE_ID.MULTIPLE_CHOICE,
		label: "Multiple Choice",
		icon: "multiple-choice",
	},
	{
		value: QUESTION_TYPE_ID.DROP_DOWN,
		label: "Drop-down",
		icon: "dropdown",
	},
	{
		value: QUESTION_TYPE_ID.TABLE,
		label: "Table",
		icon: "columns",
	},
	{
		value: QUESTION_TYPE_ID.TABLE_ROWS,
		label: "Table Headings with Supplier responding with 1 or more rows",
		icon: "columns",
	},
	{
		value: QUESTION_TYPE_ID.DATE_SELECTION,
		label: "Date Selection",
		icon: "date-selection",
	},
	{
		value: QUESTION_TYPE_ID.ADDRESS_FINDER,
		label: "Address Finder",
		icon: "address-finder",
	},
	{
		value: QUESTION_TYPE_ID.PROJECT_EXPERIENCE,
		label: "Project Experience",
		icon: "stars",
	},
	{
		value: QUESTION_TYPE_ID.KEY_PERSONNEL,
		label: "Key Personnel",
		icon: "stars",
	},
	{
		value: QUESTION_TYPE_ID.CORPORATE_DOCUMENTS,
		label: "Corporate Documents",
		icon: "stars",
	},
	{
		value: QUESTION_TYPE_ID.FILE_UPLOAD,
		label: "File Upload",
		icon: "upload-file",
	},
];

const TextAnswerContent = ({ textLength }) => {
	const maxLength = rfxSubmission.helpers.getTextLengthValue(textLength);
	return (
		<Flex
			gap={4}
			className="my-4 text-sm font-normal"
			align={"center"}
		>
			<div>
				<p className="text-primary-900">{textLength}-answer text</p>
			</div>
			<div>
				<p className="text-tertiary-600">Maximum of {maxLength} characters.</p>
			</div>
		</Flex>
	);
};

export const MultipleChoiceContent = ({ fieldname, type }) => {
	const [, meta, helper] = useField(`${fieldname("fields")}`);
	const hasError = meta?.error && meta?.touched;

	useEffect(() => {
		if (
			!meta.value ||
			typeof meta.value === "string" ||
			meta?.value?.length === 0 ||
			typeof meta?.value[0] === "object"
		) {
			helper.setValue([""]);
		}
		
	}, [meta]);

	let types;
	switch (type) {
		case QUESTION_TYPE_ID.SINGLE_OPTION:
			types = "Option";
			break;
		case QUESTION_TYPE_ID.MULTIPLE_CHOICE:
			types = "Checkbox";
			break;
		case QUESTION_TYPE_ID.DROP_DOWN:
			types = "Selection";
			break;
		default:
			break;
	}

	const deleteOptionHandler = (index) => {
		if (index > 0) {
			let newQuestions = [...meta.value];
			newQuestions.splice(index, 1);
			helper.setValue(newQuestions);
		}
	};

	const addOptionHandler = () => {
		helper.setValue([...meta.value, ""]);
	};

	const isInvalid =
		!meta?.value || !(meta?.value instanceof Array) || meta?.value.length === 0;

	return (
		<div className="space-y-1.5">
			{/* {meta?.error ? (
        <div className="text-red-500 text-md my-2">Please ensure you've added at least 1 {types}</div>
      ) : null}
       -- this error handling not handle empty options name and since multiple choice is already have default 1 options */}

			{meta?.value &&
				meta?.value instanceof Array &&
				meta?.value.length > 0 &&
				meta?.value.map((item, index) => {
					return (
						<Flex
							gap={4}
							className="text-sm font-normal"
							align={hasError ? "start" : "center"}
							key={`${fieldname("fields")}-${index}`}
						>
							<FieldForm
								placeholder={`${types} ${index + 1}`}
								name={`${fieldname("fields")}[${index}]`}
							/>
							{index > 0 ? (
								<Button
									type="button"
									btntype="linkDanger"
									className="inline-flex cursor-pointer items-center rounded-md"
									onClick={() => deleteOptionHandler(index)}
								>
									<Icon
										icon="close"
										className="ml-1"
									/>
								</Button>
							) : null}
						</Flex>
					);
				})}

			<Button
				type="button"
				btntype="link"
				gap={2}
				align="center"
				className="!my-4 !mb-0 inline-flex cursor-pointer"
				onClick={addOptionHandler}
				data-testid={`${fieldname("option")}`}
			>
				<Icon
					icon="plus"
					style={{ color: "#0045F5", width: "18px", height: "18px" }}
				/>
				<span className="text-sm font-medium">Add {types}</span>
			</Button>
			{hasError && isInvalid && (
				<span className="text-red-600">Please enter at least one option</span>
			)}
		</div>
	);
};

export const DateSelectionContent = () => {
	return (
		<div>
			<Flex
				gap={4}
				className="my-4 text-sm font-normal"
				align={"center"}
			>
				<div>
					<p className="text-primary-900">Single-date selection</p>
				</div>
				<div>
					<p className="text-tertiary-600">
						User can select one date from the calendar
					</p>
				</div>
			</Flex>
		</div>
	);
};
export const AddressFinderContent = () => {
	return (
		<Flex
			gap={4}
			className="my-4 text-sm font-normal"
			align={"center"}
		>
			<div>
				<p className="text-primary-900">Search address input field</p>
			</div>
			<div>
				<p className="text-tertiary-600">
					User can enter and search for an address
				</p>
			</div>
		</Flex>
	);
};
export const FileUploadContent = () => {
	return (
		<Flex
			gap={4}
			className="my-4"
			align={"center"}
		>
			<div>
				<p className="text-tertiary-600">User can provide a file upload</p>
			</div>
		</Flex>
	);
};

export const QuestionComponent = ({ index, sectionIndex, question }) => {
	const [questionerType, setQuestionerType] = useState(question.type);
	const fieldname = (name) =>
		`sections[${sectionIndex}]questionnaire[${index}]${name}`;
	const [field, meta, helpers] = useField(fieldname(""));
	const [, , helper] = useField(fieldname("type"));
	const [, , { setError: setCurrentQuestionFieldsError }] = useField(
		fieldname("fields")
	);

	const formik = useFormikContext();

	const changeHandler = (val) => {
		if (
			val.value === QUESTION_TYPE_ID.MULTIPLE_CHOICE ||
			val.value === QUESTION_TYPE_ID.SINGLE_OPTION ||
			val.value === QUESTION_TYPE_ID.TABLE ||
			val.value === QUESTION_TYPE_ID.DROP_DOWN
		) {
			setCurrentQuestionFieldsError(null);
		}

		// ensure hascomment is removed for paragraoh or short text
		if (
			val.value === QUESTION_TYPE_ID.PARAGRAPH ||
			val.value === QUESTION_TYPE_ID.SHORT_ANSWER
		) {
			const currentValue = field?.value;
			formik.setFieldValue(fieldname(""), { ...currentValue, hasComment: 0 });
		}

		setQuestionerType(val.value);
		const defaultValue = getDefaultValueForQuestionType(val.value);
		formik.setFieldValue(fieldname("fields"), defaultValue);
		helper.setValue(val.value);
	};

	useEffect(() => {
		setQuestionerType(question?.type);
	}, [question]);

	const renderContent = (questionerType, fieldname) => {
		switch (questionerType) {
			case QUESTION_TYPE_ID.SHORT_ANSWER:
				return <TextAnswerContent textLength={TextLength.SHORT} />;
			case QUESTION_TYPE_ID.PARAGRAPH:
				return <TextAnswerContent textLength={TextLength.LONG} />;
			case QUESTION_TYPE_ID.SINGLE_OPTION:
				return (
					<MultipleChoiceContent
						fieldname={fieldname}
						type={questionerType}
					/>
				);
			case QUESTION_TYPE_ID.MULTIPLE_CHOICE:
				return (
					<MultipleChoiceContent
						fieldname={fieldname}
						type={questionerType}
					/>
				);
			case QUESTION_TYPE_ID.DROP_DOWN:
				return (
					<MultipleChoiceContent
						fieldname={fieldname}
						type={questionerType}
					/>
				);
			case QUESTION_TYPE_ID.DATE_SELECTION:
				return <DateSelectionContent />;
			case QUESTION_TYPE_ID.ADDRESS_FINDER:
				return <AddressFinderContent />;
			case QUESTION_TYPE_ID.PROJECT_EXPERIENCE:
				return (
					<SmartContent smartFieldId={QUESTION_TYPE_ID.PROJECT_EXPERIENCE} />
				);
			case QUESTION_TYPE_ID.KEY_PERSONNEL:
				return <SmartContent smartFieldId={QUESTION_TYPE_ID.KEY_PERSONNEL} />;
			case QUESTION_TYPE_ID.CORPORATE_DOCUMENTS:
				return (
					<SmartContent
						smartFieldId={QUESTION_TYPE_ID.CORPORATE_DOCUMENTS}
						formik={formik}
						name={fieldname}
					/>
				);
			case QUESTION_TYPE_ID.FILE_UPLOAD:
				return <FileUploadContent />;
			case QUESTION_TYPE_ID.TABLE:
				return <QuestionTypeTable fieldName={fieldname} />;
			case QUESTION_TYPE_ID.TABLE_ROWS:
				return <QuestionTypeTableRowResponse fieldName={fieldname} />;
			default:
				return;
		}
	};

	return (
		<div>
			<Flex
				direction={"column"}
				gap={5}
				align={"baseline"}
				className="mb-3"
			>
				<Input
					type="select"
					name="section"
					placeholder="Select question type"
					options={questionsOptions}
					checkIcon={true}
					isContainImg={true}
					className="w-1/3"
					required
					onChange={changeHandler}
					value={questionerType}
					testId={`sections[${sectionIndex}]questionnaire[${index}]type`}
				/>
				<FieldForm
					type="textArea"
					required
					placeholder="Add a Question/Request: E.g. Tenderers are required to prepare and attach a program for the Works."
					name={fieldname("name")}
				/>
			</Flex>
			{renderContent(questionerType, fieldname)}
		</div>
	);
};
